import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import { caseStudyPath } from '../data/CaseStudies';
import { InsightArticle, insightPath, insights } from '../data/Insights';

function InsightArticlePage({ article }: { article: InsightArticle }) {
  const others = insights.filter((item) => item.id !== article.id);
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 }, '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}
    >
      <MuiLink
        component={Link}
        to='/insights'
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 3, fontWeight: 700 }}
      >
        <ArrowBackIcon fontSize='small' />
        All insights
      </MuiLink>

      <Box
        component='article'
        aria-labelledby={`${article.id}-title`}
        sx={{
          border: '1px solid rgba(11, 37, 69, 0.14)',
          borderRadius: 3,
          minWidth: 0,
          p: { xs: 3, md: 5 },
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          variant='overline'
          sx={{ color: '#005596', fontWeight: 700, overflowWrap: 'anywhere' }}
        >
          {article.topic}
        </Typography>
        <Typography
          component='h1'
          id={`${article.id}-title`}
          variant='h3'
          aria-label={`${article.title} — ${article.topic}`}
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 800,
            color: '#0B2545',
            fontSize: { xs: '2rem', md: '3rem' },
            overflowWrap: 'anywhere',
          }}
        >
          {article.title}
        </Typography>
        <Typography
          variant='body1'
          sx={{ color: '#0B2545', fontWeight: 500, maxWidth: '72ch' }}
        >
          {article.summary}
        </Typography>

        {article.sections.map((section, index) => {
          const sectionId = `${article.id}-section-${index + 1}`;
          return (
            <Box
              component='section'
              key={section.heading}
              aria-labelledby={sectionId}
              sx={{ mt: 4 }}
            >
              <Typography
                component='h2'
                id={sectionId}
                variant='h5'
                sx={{ mb: 1.5, fontWeight: 700, color: '#0B2545', overflowWrap: 'anywhere' }}
              >
                {section.heading}
              </Typography>
              {section.paragraphs.map((paragraph) => (
                <Typography
                  key={paragraph}
                  variant='body1'
                  sx={{ mt: 1.5, color: '#475569', maxWidth: '72ch' }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          );
        })}

        <Box
          component='section'
          aria-labelledby={`${article.id}-related-work`}
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 2,
            bgcolor: '#EFF6FC',
            borderLeft: '4px solid #005596',
          }}
        >
          <Typography
            component='h2'
            id={`${article.id}-related-work`}
            variant='h6'
            sx={{ mb: 1, fontWeight: 700, color: '#0B2545' }}
          >
            Related delivered work
          </Typography>
          <Box
            component='ul'
            sx={{ m: 0, pl: 3, display: 'grid', gap: 1 }}
          >
            {article.relatedCaseStudies.map((study) => (
              <li key={study.studyId}>
                <MuiLink
                  component={Link}
                  to={caseStudyPath(study.studyId)}
                  sx={{ fontWeight: 700 }}
                >
                  {study.title}
                </MuiLink>{' '}
                <Typography
                  component='span'
                  variant='body2'
                  sx={{ color: '#0B2545' }}
                >
                  ({study.attribution})
                </Typography>
              </li>
            ))}
            {article.relatedPage && (
              <li>
                <MuiLink
                  component={Link}
                  to={article.relatedPage.path}
                  sx={{ fontWeight: 700 }}
                >
                  {article.relatedPage.label}
                </MuiLink>
              </li>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        component='nav'
        aria-label='More insights'
        sx={{ mt: 5 }}
      >
        <Typography
          component='h2'
          variant='h5'
          sx={{ mb: 1, fontWeight: 700, color: '#0B2545' }}
        >
          More insights
        </Typography>
        <Box
          component='ul'
          sx={{ m: 0, pl: 3, display: 'grid', gap: 1 }}
        >
          {others.map((item) => (
            <li key={item.id}>
              <MuiLink
                component={Link}
                to={insightPath(item.id)}
              >
                {item.title}
              </MuiLink>
            </li>
          ))}
        </Box>
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default InsightArticlePage;
